import React, { useState, useEffect } from "react";
import { useUI } from "../../context/UIContext";
import { ReportAPI } from "../../api/apiClient";

interface LookAheadItem {
  levelNode: number;
  workGroupNo: number;
  workGroup: string;
  workNo: number;
  work: string;
  transaction: string;
  trxQty: number;
  achieved: number;
  target: number;
  achievedPercent: number;
  targetPercent: number;
}

export default function LookAheadProgressReport() {
  const { selectedProjectId } = useUI();

  const [data, setData] = useState<LookAheadItem[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const loadReport = async () => {
      if (!selectedProjectId) return;

      try {
        setLoading(true);

        const response =
          await ReportAPI.getLookAheadProgressReport(
            -1 * selectedProjectId,
            0
          );

        setData(response);
      } catch {
        alert("Failed to load report");
      } finally {
        setLoading(false);
      }
    };

    loadReport();
  }, [selectedProjectId]);

  // Group by WorkGroup
  const grouped = data.reduce((acc: any, item) => {
    if (!acc[item.workGroup]) acc[item.workGroup] = [];
    acc[item.workGroup].push(item);
    return acc;
  }, {});

  return (
    //<div className="bg-white rounded-xl shadow-sm border flex flex-col h-full overflow-hidden">
    <div className="bg-white rounded-xl shadow-sm border overflow-hidden">

      {/* ===== HEADER ===== */}
     {/* <div className="bg-blue-800 text-white font-semibold px-4 py-2 text-sm">*/}
     <div className="bg-[#1788ac] text-white font-semibold px-4 py-2 text-sm">

        STATUS AS PER WEEKLY MEETING
      </div>

      {/* ===== BODY ===== */}
      <div className="p-4 flex-1 overflow-y-auto">

        {loading && (
          <div className="text-sm text-gray-500">
            Loading...
          </div>
        )}

        {!loading && data.length > 0 && (
          <>
            {Object.keys(grouped).map((groupName, idx) => (
              <div key={idx} className="mb-4">

                {/* WorkGroup Header */}
                <div className="bg-gray-200 text-xs font-medium px-2 py-1 rounded">
                  {groupName}
                </div>

                {grouped[groupName].map((row: LookAheadItem, i: number) => {

                  const type = row.transaction.trim();

                  // ===== WIDTH CALCULATION =====
                  let finalWidth = 0;

                  if (type === "Percent") {
                    finalWidth =
                      row.target === 0
                        ? 0
                        : (row.achieved / row.target) * 100;
                  } else {
                    finalWidth =
                      row.trxQty === 0
                        ? 0
                        : (row.achieved / row.trxQty) * 100;
                  }

                  finalWidth = Math.min(finalWidth, 100);

                  // ===== COLOR LOGIC =====
                  let achievedColor = "#f97316"; // orange default

                  const isCompleted =
                    (type === "Percent" &&
                      row.achieved >= row.target &&
                      row.target > 0) ||
                    (type !== "Percent" &&
                      row.achieved >= row.trxQty &&
                      row.trxQty > 0);

                  if (isCompleted) {
                    achievedColor = "#1f8f3a"; // green
                  }

                  return (
                    <div key={i} className="mt-3 text-xs">

                      {/* Work Row */}
                      <div className="flex justify-between mb-1">
                        <div className="whitespace-pre-line">
                          {row.work}
                        </div>
                        <div className="text-gray-600">
                       {/*   {row.achieved} of {row.target} {type} */}
                       {Math.round(row.achieved)} of {Math.round(row.target)} {type}
                        </div>
                      </div>

                      {/* ===== DESKTOP STYLE STACKED BARS ===== */}
                      <div className="mt-1">

                        {/* 🔵 FULL SCOPE BAR */}
                        <div className="h-[6px] bg-[#5c88d6] rounded-sm" />

                        {/* 🟠 / 🟢 ACHIEVED BAR BELOW */}
                        {row.achieved > 0 && (
                          <div
                            className="h-[6px] mt-1 rounded-sm"
                            style={{
                              width: `${finalWidth}%`,
                              backgroundColor: achievedColor
                            }}
                          />
                        )}

                      </div>

                    </div>
                  );
                })}

              </div>
            ))}
          </>
        )}

        {!loading && data.length === 0 && (
          <div className="text-sm text-gray-400">
            No data found
          </div>
        )}

      </div>
    </div>
  );
}